import React, { useState } from 'react';
import type { TeamMember, TeamRole, User } from '../../../../types';
import { teamService } from '../../services/teamService';
import { useDialog } from '../../../../hooks/useDialog';

interface MemberListProps {
  members: TeamMember[];
  currentUser: User;
  userRole: TeamRole;
  onMemberUpdate: () => void;
}

export const MemberList: React.FC<MemberListProps> = ({
  members = [],
  currentUser,
  userRole,
  onMemberUpdate
}) => {
  const [loading, setLoading] = useState<string | null>(null);
  const { confirm } = useDialog();

  const canManage = userRole === 'OWNER' || userRole === 'ADMIN';

  const getRoleLabel = (role: TeamRole) => {
    switch (role) {
      case 'OWNER': return '所有者';
      case 'ADMIN': return '管理员';
      case 'MEMBER': return '成员';
      default: return role;
    }
  };

  const getRoleColor = (role: TeamRole) => {
    switch (role) {
      case 'OWNER': return 'text-red-600 bg-red-50';
      case 'ADMIN': return 'text-orange-600 bg-orange-50';
      case 'MEMBER': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleRoleChange = async (member: TeamMember, newRole: TeamRole) => {
    const action = newRole === 'ADMIN' ? '提升为管理员' : '降级为普通成员';
    const confirmed = await confirm({
      title: '确认',
      message: `确定要将 ${member.user?.username || member.user?.name || '该成员'} ${action}吗？`,
      confirmVariant: 'primary'
    });
    if (!confirmed) return;

    setLoading(member.id);
    try {
      await teamService.updateMemberRole(member.teamId, member.userId, newRole);
      onMemberUpdate();
      console.log(`成员角色更新成功`);
    } catch (error) {
      console.error('更新成员角色失败:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    const confirmed = await confirm({
      title: '确认',
      message: `确定要将 ${member.user?.username || member.user?.name || '该成员'} 从团队中移除吗？此操作不可撤销。`,
      confirmVariant: 'danger'
    });
    if (!confirmed) return;

    setLoading(member.id);
    try {
      await teamService.removeMember(member.teamId, member.userId);
      onMemberUpdate();
      console.log(`成员移除成功`);
    } catch (error) {
      console.error('移除成员失败:', error);
    } finally {
      setLoading(null);
    }
  };

  // 安全处理成员数组
  if (!members || members.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">团队成员</h3>
          <div className="text-xs text-gray-500">
            共 0 人
          </div>
        </div>
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-sm">暂无团队成员</p>
        </div>
      </div>
    );
  }

  const sortedMembers = [...members].sort((a, b) => {
    // 按角色排序：OWNER > ADMIN > MEMBER
    const roleOrder = { 'OWNER': 0, 'ADMIN': 1, 'MEMBER': 2 };
    return (roleOrder[a.role] || 2) - (roleOrder[b.role] || 2);
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">团队成员</h3>
        <div className="text-xs text-gray-500">
          共 {members.length} 人
        </div>
      </div>
      
      <div className="space-y-2">
        {sortedMembers.map(member => {
          // 安全检查member.user是否存在
          if (!member.user) {
            return null;
          }
          
          const isCurrentUser = member.userId === currentUser.id;
          const canManageThisMember = canManage && member.role !== 'OWNER' && !isCurrentUser;
          const isLoading = loading === member.id;

          return (
            <div 
              key={member.id} 
              className={`flex items-center justify-between p-3 bg-white rounded-lg border transition-colors duration-200 ${
                isCurrentUser ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                {/* 头像 */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                  member.role === 'OWNER' ? 'bg-red-500' : 
                  member.role === 'ADMIN' ? 'bg-orange-500' : 'bg-blue-500'
                }`}>
                  {(member.user?.username || member.user?.name || '?')[0].toUpperCase()}
                </div>
                
                {/* 用户信息 */}
                <div className="ml-3">
                  <div className="flex items-center">
                    <p className="font-medium text-gray-900">
                      {member.user?.username || member.user?.name || '未知用户'}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs text-blue-500">(我)</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                      {getRoleLabel(member.role)}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      {new Date(member.joinedAt).toLocaleDateString('zh-CN')} 加入
                    </span>
                  </div>
                </div>
              </div>
              
              {/* 操作按钮 */}
              {canManageThisMember && (
                <div className="flex items-center space-x-2">
                  {isLoading ? (
                    <div className="flex items-center px-3 py-1 text-gray-500">
                      <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      处理中...
                    </div>
                  ) : (
                    <>
                      {/* 角色切换按钮 */}
                      <button 
                        onClick={() => handleRoleChange(
                          member, 
                          member.role === 'MEMBER' ? 'ADMIN' : 'MEMBER'
                        )}
                        className="text-xs px-2 py-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-200"
                      >
                        {member.role === 'MEMBER' ? (
                          <div className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                            提升
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                            降级
                          </div>
                        )}
                      </button>
                      
                      {/* 移除按钮 */}
                      <button 
                        onClick={() => handleRemoveMember(member)}
                        className="text-xs px-2 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors duration-200"
                      >
                        <div className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          移除
                        </div>
                      </button>
                    </>
                  )}
                </div>
              )}
              
              {!canManage && member.role === 'OWNER' && (
                <div className="text-xs text-gray-500">团队创建者</div>
              )}
            </div>
          );
        })}
      </div>
      
      {!canManage && (
        <div className="mt-3 text-xs text-gray-500 text-center">
          💡 只有团队所有者和管理员可以管理成员
        </div>
      )}
    </div>
  );
};